import type { TypesenseSearchParams } from '~/utils/typesenseClient'
import type { GeoCoords } from '~/utils/typesenseGeo'
import {
  getFederatedSearchIndexName,
  getLocationsIndexName,
} from '~/utils/searchEngineHelpers'

/**
 * The city the homepage hero search is scoped to (KFT-4205). A `locations`
 * document reduced to what the search and the city line need; `name` stays
 * the raw localized object so the line follows a locale switch.
 */
export interface SearchCity {
  slug: string
  type: string
  name: string | Record<string, string>
  eventCount?: number
}

/**
 * Where the selected city came from — drives the city line caption:
 * `ip` "in Deiner Nähe", `user` none, `none` "Überall suchen".
 */
export type SearchCitySource = 'ip' | 'user' | 'none'

/** The viewer's explicit choice, remembered across visits. `null` = everywhere. */
export interface StoredSearchCity {
  city: SearchCity | null
}

export const SEARCH_CITY_STORAGE_KEY = 'kft-search-city'

/**
 * Radius around the viewer's IP coordinates to look for their city. Wide enough
 * to land suburbs on the nearby big city, narrow enough that a viewer abroad
 * gets no match ("Überall suchen") rather than a far-away German one.
 */
export const IP_CITY_RADIUS_KM = 25

const LOCATIONS_QUERY_BY = 'name.de_DE,name.en_US,slug'
const LOCATIONS_INCLUDE_FIELDS = 'slug,type,name,event_count'

// Cities with nothing bookable are noise in a picker that scopes the search.
const PICKABLE_FILTER = 'type:!=admin_area && event_count:>0'

/** Picker list: prefix match on the typed text, cities with the most events first. */
export const buildCityPickerSearch = (
  query: string,
  perPage: number
): TypesenseSearchParams => ({
  collection: getLocationsIndexName(),
  q: query.trim() || '*',
  query_by: LOCATIONS_QUERY_BY,
  per_page: perPage,
  // Untyped, the list sits under "Städte": regions ("Rhein", 1331 events)
  // would outrank München there. Typing finds regions too.
  filter_by: query.trim()
    ? PICKABLE_FILTER
    : `${PICKABLE_FILTER} && type:=locality`,
  sort_by: query.trim()
    ? '_text_match:desc,event_count:desc'
    : 'event_count:desc',
  include_fields: LOCATIONS_INCLUDE_FIELDS,
})

/**
 * The viewer's city: the locality with the most events within
 * `IP_CITY_RADIUS_KM` — the nearest one is often a village with three events
 * next to München.
 */
export const buildIpCitySearch = (
  coords: GeoCoords
): TypesenseSearchParams => ({
  collection: getLocationsIndexName(),
  q: '*',
  query_by: 'slug',
  per_page: 1,
  filter_by: `type:=locality && event_count:>0 && _geoloc:(${coords.lat},${coords.lng},${IP_CITY_RADIUS_KM} km)`,
  sort_by: 'event_count:desc',
  include_fields: LOCATIONS_INCLUDE_FIELDS,
})

export const toSearchCity = (doc: any): SearchCity | null =>
  doc?.slug
    ? {
        slug: doc.slug,
        type: doc.type ?? 'locality',
        name: doc.name ?? doc.slug,
        // `event_count` from Typesense, `eventCount` from localStorage.
        eventCount: doc.event_count ?? doc.eventCount,
      }
    : null

/**
 * Count of all events, for the picker's "Überall" row — every other row's
 * number is an event count too. Counted on the events collection, not summed
 * from the cities: an event in two cities would count twice.
 */
export const buildEventsTotalSearch = (
  locale: string
): TypesenseSearchParams => ({
  collection: getFederatedSearchIndexName(locale),
  q: '*',
  query_by: 'title',
  per_page: 0,
})

/** Parse the remembered choice; anything malformed counts as no choice. */
export const parseStoredSearchCity = (
  raw: string | null
): StoredSearchCity | undefined => {
  if (!raw) return undefined
  try {
    const parsed = JSON.parse(raw)
    if (parsed?.city === null) return { city: null }
    const city = toSearchCity(parsed?.city)
    return city ? { city } : undefined
  } catch {
    return undefined
  }
}

/** Split `name` around a case-insensitive prefix match, for the picker's bold prefix. */
export const splitPrefixMatch = (
  name: string,
  query: string
): { match: string; rest: string } => {
  const q = query.trim()
  if (!q || !name.toLowerCase().startsWith(q.toLowerCase())) {
    return { match: '', rest: name }
  }
  return { match: name.slice(0, q.length), rest: name.slice(q.length) }
}

/** `/search` params that scope the results page to the city, as a location pick does. */
export const searchCityQueryParams = (
  city: SearchCity | null | undefined
): Record<string, string> =>
  city ? { location: city.slug, locationType: city.type } : {}
